/**
 * El aviso de Business Central de un cliente que quedó sin las dimensiones que
 * exige el registro.
 *
 * Caso 123: sin caja abierta BC aplica la plantilla de Conf. ventas y cobros, y si
 * a esa plantilla le faltan dimensiones --`CANAL_VENTA`, por ejemplo-- el cliente
 * sale «creado» y su primera venta se rechaza al registrar: «The CANAL_VENTA
 * dimension is the default dimension, and it must have a value».
 *
 * Desde la BC 1.0.4.112, el alta avisa pegándole al `Mensaje` de
 * `insertaClientes` un `' Aviso: CLIENTE_SIN_DIMENSIONES: …'`, con `Ok=true`. El
 * mensaje sigue viajando entero al cuadro de sincronización; esto lo reconoce por
 * su código para guardarlo en la fila del cliente y avisar al elegirlo en una
 * venta. No se bloquea: el dato se corrige en BC y la copia local no se entera.
 *
 * Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, fila H5 · TPV.
 */

export const CODIGO_SIN_DIMENSIONES = "CLIENTE_SIN_DIMENSIONES";

/**
 * El tramo del mensaje de BC que corresponde al aviso, o `null` si no lo trae.
 *
 * Va desde el código hasta el siguiente `Aviso:` --BC puede concatenar también el
 * de la caja o la tienda que no resolvió-- o hasta el final.
 *
 * @param {string|null|undefined} mensaje
 * @returns {string|null}
 */
export const avisoDeDimensiones = (mensaje) => {
  const completo = String(mensaje ?? "");
  const inicio = completo.indexOf(CODIGO_SIN_DIMENSIONES);
  if (inicio < 0) return null;

  const resto = completo.slice(inicio);
  const siguiente = resto.slice(CODIGO_SIN_DIMENSIONES.length).search(/\s+Aviso:/i);
  const tramo = siguiente < 0 ? resto : resto.slice(0, CODIGO_SIN_DIMENSIONES.length + siguiente);

  return tramo.trim();
};

/** Lo que dice el aviso después del código, sin el punto final. Puede ser "". */
export const dimensionesDelAviso = (aviso) =>
  String(aviso ?? "")
    .replace(CODIGO_SIN_DIMENSIONES, "")
    .replace(/^\s*:\s*/, "")
    .replace(/[\s.…]+$/, "")
    .trim();

/**
 * El texto para el cajero al elegir el cliente, o `null` si no hay aviso.
 *
 * @param {{avisoDeBC?: string|null}|null|undefined} cliente
 * @returns {string|null}
 */
export const advertenciaDeDimensiones = (cliente) => {
  const aviso = avisoDeDimensiones(cliente?.avisoDeBC);
  if (!aviso) return null;

  const dimensiones = dimensionesDelAviso(aviso);
  const cuales = dimensiones ? ` (${dimensiones})` : "";

  return `Este cliente no tiene las dimensiones que exige Business Central${cuales}. La venta no se podrá registrar hasta que las completen.`;
};
